import { Logger } from '@frontend/logger';

import { AuthenticationProvider } from './authentication-provider';
import { BasicAuthenticationClient } from './basic-authentication-client';
import DriverAuthenticationClient from './driver-authentication-client';
import { IoTProvision, Token } from './models';
import { TokenRefreshScheduler } from './token-refresh-scheduler';

export class DriverAuthenticationManager extends AuthenticationProvider {
    private static instance: DriverAuthenticationManager | null;
    public provisionInfo?: IoTProvision;
    public userLoggedIn: boolean;
    private readonly refreshScheduler = new TokenRefreshScheduler();

    private constructor(location?: string) {
        super(location ?? 'driver_token');
        this.userLoggedIn = false;
    }

    public static getInstance = (location?: string): DriverAuthenticationManager => {
        if (!DriverAuthenticationManager.instance) {
            DriverAuthenticationManager.instance = new DriverAuthenticationManager(location);
        }
        return DriverAuthenticationManager.instance;
    };

    /**
     * Registers provision info and schedules refresh for an already stored kiosk token.
     * Use when the app reloads with a valid token and skips re-authentication.
     */
    registerProvision(provision: IoTProvision): void {
        this.provisionInfo = provision;
        const token = this.getTokenFromStorage();
        if (token) {
            this.scheduleTokenRefresh(token);
        }
    }

    async authenticate(provision: IoTProvision): Promise<Token> {
        this.provisionInfo = provision;
        const credentials = await DriverAuthenticationClient.provision(provision);
        if (credentials.context != null) this.userLoggedIn = true;
        this.saveTokenToStorage(credentials);
        Logger.log(credentials);
        this.loggedIn = true;

        return credentials;
    }

    /**
     * Refreshes the kiosk device token via authorization /v1/refresh.
     * Falls back to re-provision when refresh fails and provision info is available.
     */
    async refresh(): Promise<void> {
        return this.refreshToken();
    }

    protected override saveTokenToStorage(token: Token): void {
        super.saveTokenToStorage(token);
        this.scheduleTokenRefresh(token);
    }

    override async logout(callback?: (() => any) | undefined): Promise<void> {
        this.refreshScheduler.clear();

        try {
            const token = await this.waitForToken();
            await BasicAuthenticationClient.logout(token.jwt_token);
            this.userLoggedIn = false;
            super.logout(callback);
        } catch (error) {
            Logger.error('Something went wrong trying to logout.', error);
        }
    }

    private scheduleTokenRefresh(token: Token): void {
        this.refreshScheduler.schedule(token, () => this.refreshToken(), 'Kiosk');
    }

    private async refreshToken(): Promise<void> {
        const token = this.getTokenFromStorage();
        if (!token?.refresh_token) {
            Logger.error('Cannot refresh kiosk token: refresh token not available');
            return;
        }

        Logger.log('Kiosk token expiring soon, refreshing...');
        try {
            const credentials = await BasicAuthenticationClient.refresh(token.refresh_token);
            if ((credentials as any).detail) throw new Error((credentials as any).detail);
            this.saveTokenToStorage(credentials);
            this.loggedIn = true;
        } catch (error) {
            Logger.error('Failed to refresh kiosk token:', error);
            if (this.provisionInfo) {
                Logger.log('Falling back to kiosk re-provision after refresh failure');
                try {
                    await this.authenticate(this.provisionInfo);
                } catch (provisionError) {
                    Logger.error('Failed to re-provision kiosk token:', provisionError);
                }
            }
        }
    }
}
